import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/constants';
import {
  Loader2, Copy, Check, Share2, Trophy, Users, ArrowRight, Calendar,
  MapPin, Megaphone, Zap, ShieldCheck, Eye, X as XIcon
} from 'lucide-react';

const initials = (name) => (name || '?').trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
const posLabel = (p) => Array.isArray(p?.positions) ? p.positions.join(' / ') : (p?.positions || '');
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ClaimModal = ({ player, onClose }) => {
  const [email, setEmail] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState('');

  const submit = async () => {
    if (!email.trim() && !xHandle.trim()) { toast.error('Add your email or X handle so we can reach you'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/team/claim-request`, {
        player_user_id: player.user_id,
        contact_email: email.trim() || null,
        contact_x: xHandle.trim() || null,
        message: message.trim() || null,
      });
      setDoneMsg(res.data.message || 'Offersz HQ will reach out to verify it\'s you and send your claim code.');
      setDone(true);
      toast.success(res.data.message || 'Request sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send request');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="team-claim-modal">
      <div className="bg-[#0d0f0e] border border-white/10 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 sm:p-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {player.profile_picture_url
              ? <img src={player.profile_picture_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              : <div className="w-12 h-12 rounded-full bg-[#16A34A] flex items-center justify-center font-black text-white">{initials(player.name)}</div>}
            <div><p className="font-black text-white text-lg leading-tight">{player.name}</p><p className="text-white/40 text-xs">{posLabel(player)}</p></div>
          </div>
          <button onClick={onClose} data-testid="claim-modal-close" className="text-white/40 hover:text-white p-1"><XIcon className="h-5 w-5" /></button>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="claim-request-done">
            <div className="w-14 h-14 rounded-full bg-[#16A34A]/20 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-[#4ade80]" /></div>
            <p className="text-white font-bold mb-1">Request sent!</p>
            <p className="text-white/50 text-sm">{doneMsg}</p>
            <button onClick={onClose} className="mt-5 w-full bg-white/10 hover:bg-white/15 text-white font-bold py-3 rounded-full">Done</button>
          </div>
        ) : (
          <>
            <p className="text-white/50 text-sm mb-4">Is this you? Request your claim code and we'll verify it's really you, then send it over.</p>
            <label className="text-xs font-bold text-white/60 uppercase tracking-wider">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@email.com" data-testid="claim-email-input"
              className="w-full mt-1 mb-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#16A34A] outline-none" />
            <label className="text-xs font-bold text-white/60 uppercase tracking-wider">X / Twitter handle (optional)</label>
            <input value={xHandle} onChange={e => setXHandle(e.target.value)} placeholder="@yourhandle" data-testid="claim-x-input"
              className="w-full mt-1 mb-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#16A34A] outline-none" />
            <label className="text-xs font-bold text-white/60 uppercase tracking-wider">Note (optional)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="Anything we should know" data-testid="claim-message-input"
              className="w-full mt-1 mb-4 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#16A34A] outline-none resize-none" />
            <button onClick={submit} disabled={sending} data-testid="claim-request-submit"
              className="w-full bg-[#0F5132] hover:bg-[#0B3B24] disabled:opacity-60 text-white font-black py-3.5 rounded-full flex items-center justify-center gap-2">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Request my claim code <ArrowRight className="h-4 w-4" /></>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const TeamPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [capCopied, setCapCopied] = useState(false);
  const [claimPlayer, setClaimPlayer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/team/${slug}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#090b0a] flex items-center justify-center"><Loader2 className="h-8 w-8 text-[#16A34A] animate-spin" /></div>;
  if (!data) return (
    <div className="min-h-screen bg-[#090b0a] flex flex-col items-center justify-center text-center px-6">
      <p className="text-white font-black text-2xl mb-2">Team not found</p>
      <p className="text-white/40 mb-6">This team page doesn't exist yet.</p>
      <button onClick={() => navigate('/')} className="bg-[#0F5132] text-white font-bold px-6 py-3 rounded-full">Go home</button>
    </div>
  );

  const { team, players, feed, stats } = data;
  const teamUrl = `${window.location.origin}/team/${slug}`;
  const shareUrl = `${window.location.origin}/api/team/${slug}/og.png`;
  const caption = `Follow ${team.school_name}'s recruiting & offseason on Offersz 🏈 ${teamUrl}`;

  const copyLink = () => { navigator.clipboard.writeText(teamUrl); setCopied(true); toast.success('Team link copied!'); setTimeout(() => setCopied(false), 2000); };
  const copyCaption = () => { navigator.clipboard.writeText(caption); setCapCopied(true); toast.success('Caption copied!'); setTimeout(() => setCapCopied(false), 2000); };

  return (
    <div className="min-h-screen bg-[#090b0a] text-white">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F5132]/30 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-6 pt-10 pb-8">
          <div className="flex items-center gap-5">
            {team.logo_url
              ? <img src={team.logo_url} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-contain bg-white/5 p-2 border border-white/10" data-testid="team-logo" />
              : <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#16A34A] flex items-center justify-center font-black text-3xl" data-testid="team-logo-fallback">{initials(team.school_name)}</div>}
            <div className="min-w-0">
              <p className="text-[#4ade80] text-xs font-black uppercase tracking-[0.2em] mb-1">Offersz Team</p>
              <h1 className="text-3xl sm:text-5xl font-black leading-none truncate" data-testid="team-name">{team.school_name}</h1>
              <p className="text-white/40 text-sm mt-1">{team.team_name}</p>
            </div>
          </div>

          {/* Claim progress — the race */}
          <div className="mt-7 bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="team-claim-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-white/70 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#4ade80]" /> Roster claimed</span>
              <span className="text-sm font-black text-[#4ade80]">{stats.claimed}/{stats.total} · {stats.completion_percentage}%</span>
            </div>
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#16A34A] to-[#4ade80] rounded-full transition-all" style={{ width: `${stats.completion_percentage}%` }} />
            </div>
          </div>

          {/* Share kit */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={copyLink} data-testid="team-copy-link" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-sm font-bold px-4 py-2.5 rounded-full">
              {copied ? <Check className="h-4 w-4 text-[#4ade80]" /> : <Copy className="h-4 w-4" />} Copy team link
            </button>
            <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="team-share-card" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-sm font-bold px-4 py-2.5 rounded-full">
              <Share2 className="h-4 w-4" /> Share card
            </a>
            <button onClick={copyCaption} data-testid="team-copy-caption" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-sm font-bold px-4 py-2.5 rounded-full">
              {capCopied ? <Check className="h-4 w-4 text-[#4ade80]" /> : <Megaphone className="h-4 w-4" />} Copy post caption
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 grid lg:grid-cols-5 gap-8">
        {/* Roster */}
        <div className="lg:col-span-3">
          <h2 className="text-lg font-black mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#4ade80]" /> Roster <span className="text-white/30 text-sm font-bold">({stats.total})</span></h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="team-roster-grid">
            {players.map((p) => (
              <div key={p.user_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center text-center" data-testid={`team-player-${p.user_id}`}>
                {p.profile_picture_url
                  ? <img src={p.profile_picture_url} alt="" className="w-16 h-16 rounded-full object-cover mb-2" />
                  : <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center font-black text-lg mb-2">{initials(p.name)}</div>}
                <p className="font-bold text-sm leading-tight truncate w-full" title={p.name}>{p.name}</p>
                <p className="text-white/40 text-xs mb-3">{posLabel(p)}{p.class_year ? ` · '${String(p.class_year).slice(-2)}` : ''}</p>
                {p.is_live ? (
                  <button onClick={() => navigate(`/${p.username}`)} data-testid={`view-player-${p.user_id}`}
                    className="mt-auto w-full text-xs font-bold bg-[#16A34A]/15 text-[#4ade80] py-2 rounded-full flex items-center justify-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5" /> View page
                  </button>
                ) : (
                  <div className="mt-auto w-full space-y-1.5">
                    <button onClick={() => setClaimPlayer(p)} data-testid={`claim-player-${p.user_id}`}
                      className="w-full text-xs font-bold bg-white/10 hover:bg-[#0F5132] text-white py-2 rounded-full transition-colors">
                      Claim my profile
                    </button>
                    <button onClick={() => navigate(`/preview/${p.username}`)} data-testid={`preview-player-${p.user_id}`}
                      className="w-full text-xs font-bold text-[#4ade80] hover:text-[#86efac] py-1 rounded-full flex items-center justify-center gap-1">
                      <Eye className="h-3.5 w-3.5" /> Preview my page
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Team feed */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-black mb-4 flex items-center gap-2"><Zap className="h-5 w-5 text-[#4ade80]" /> Team feed</h2>
          {feed.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-white/40 text-sm" data-testid="team-feed-empty">
              No posts yet. When players share updates & events to the team, they'll show up here.
            </div>
          ) : (
            <div className="space-y-3" data-testid="team-feed">
              {feed.map((item) => (
                <div key={`${item.type}-${item.id}`} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`feed-${item.type}-${item.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {item.author?.profile_picture_url
                      ? <img src={item.author.profile_picture_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      : <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-black">{initials(item.author?.name)}</div>}
                    <button onClick={() => item.author?.username && navigate(`/${item.author.username}`)} className="text-sm font-bold hover:text-[#4ade80]">{item.author?.name}</button>
                  </div>
                  {item.type === 'post' ? (
                    <>
                      <p className="text-sm text-white/80 whitespace-pre-wrap">{item.content}</p>
                      {item.image_url && <img src={item.image_url} alt="" className="mt-2 rounded-xl w-full object-cover max-h-60" />}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-white/80 flex-wrap">
                      <Calendar className="h-4 w-4 text-[#4ade80] shrink-0" />
                      <span className="font-bold">{item.camp_name}</span>
                      {item.camp_date && <span className="text-white/40">· {fmtDate(item.camp_date)}</span>}
                      {item.location && <span className="text-white/40 flex items-center gap-1"><MapPin className="h-3 w-3" />{item.location}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="border-t border-white/5 py-8 text-center">
        <p className="text-white/40 text-sm mb-3">Not on Offersz yet?</p>
        <button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-black px-6 py-3 rounded-full inline-flex items-center gap-2">
          Build your free recruiting page <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {claimPlayer && <ClaimModal player={claimPlayer} onClose={() => setClaimPlayer(null)} />}
    </div>
  );
};

export default TeamPage;
